import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { matchesFilterableOption } from './filterMatch';

export interface FilterableSelectOption {
  /** The value the form submits when this option is picked. */
  value: string;
  /** The primary, visible text of the option (and of the closed field). */
  label: string;
  /** Optional second line under the label (e.g. a code, or tag chips as text) — also matched by the filter. */
  secondary?: string;
  /** Renders `secondary` in the monospaced face — for identifiers (UUIDs, codes) shown under a human-readable label. */
  secondaryMono?: boolean;
  /** Extra filter-only terms never rendered (e.g. raw tag values). */
  keywords?: string[];
}

interface FilterableSelectProps {
  label: string;
  options: FilterableSelectOption[];
  /** The selected option's `value`, or `''`/`null` for no selection. In `freeSolo` mode, the raw text is the value. */
  value: string | null;
  /** Fires with the picked option's `value` (`''` when cleared) and the full option (or `null`). */
  onChange: (value: string, option: FilterableSelectOption | null) => void;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Shows a progress adornment and the localized loading text while the option source resolves. */
  loading?: boolean;
  autoFocus?: boolean;
  /**
   * Combobox-over-free-text mode: whatever the operator types IS the value
   * (fired on every keystroke, like a plain `TextField`), with the option
   * list as suggestions on top. For filters whose facet population may be
   * truncated server-side — the typed text always remains submittable.
   */
  freeSolo?: boolean;
  /** Hides the clear (×) control — for required selections that always keep a value. */
  disableClearable?: boolean;
}

/**
 * The application's single-select combobox: a fixed option list the operator
 * narrows by typing, matching the primary label, the secondary text and any
 * hidden keywords (every whitespace-separated term must match somewhere).
 * Options are plain `{ value, label }` records at minimum, so a free list of
 * string values works by mapping each value to both fields. Renders as the
 * same small outlined field the dialogs' `TextField select` inputs use.
 */
export function FilterableSelect({
  label,
  options,
  value,
  onChange,
  helperText,
  required,
  disabled,
  error,
  loading,
  autoFocus,
  freeSolo,
  disableClearable,
}: FilterableSelectProps) {
  const translate = useTranslate();
  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={freeSolo ? (selected ?? (value || null)) : selected}
      inputValue={freeSolo ? (value ?? '') : undefined}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      loading={loading}
      disabled={disabled}
      onChange={(_event, option) => {
        if (typeof option === 'string') {
          onChange(option, options.find((candidate) => candidate.value === option) ?? null);
          return;
        }
        onChange(option?.value ?? '', option);
      }}
      onInputChange={
        freeSolo
          ? (_event, text, reason) => {
              // 'reset' is the programmatic sync after a pick/prop change — the
              // `onChange` above already reported it; re-firing here would echo
              // the label back as a typed value.
              if (reason === 'reset') return;
              onChange(text, options.find((candidate) => candidate.value === text) ?? null);
            }
          : undefined
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, current) => option.value === (typeof current === 'string' ? current : current.value)}
      filterOptions={(candidates, state) => {
        const input = state.inputValue.trim().toLowerCase();
        return input ? candidates.filter((candidate) => matchesFilterableOption(candidate, input)) : candidates;
      }}
      noOptionsText={translate('pay.filterableSelect.noOptions')}
      loadingText={translate('pay.filterableSelect.loading')}
      renderOption={({ key, ...optionProps }, option) => (
        <li key={key} {...optionProps}>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2">{option.label}</Typography>
            {option.secondary && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={option.secondaryMono ? (theme) => ({ fontFamily: theme.custom.fontFamilyMono }) : undefined}
              >
                {option.secondary}
              </Typography>
            )}
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          required={required}
          error={error}
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
